/**
 * The walkthroughs a customer watches under their inspection photographs.
 *
 * Jonathon, 2026-09-05: "yes please build it to where we can upload videos to
 * products."
 *
 * ADR 0105 put the first three in by migration and said out loud that adding a
 * fourth needed an upload and another migration — fine at three, wrong at ten.
 * This is the tenth one's screen.
 *
 * ## The still is taken, not asked for
 *
 * A video needs a poster or the customer sees a black rectangle with a play
 * button on it. Asking a rep for a second file means most videos would have no
 * poster, so the browser takes one: the video is decoded far enough to seek a
 * second in, and a frame is drawn to a canvas. Uploading your own still is
 * offered for the case where the first second is somebody's boot.
 *
 * A capture that fails is not an error. Some phone recordings will not decode
 * in a canvas at all, and a video with no poster still plays — it just opens
 * dark. Losing the poster must never lose the upload.
 */

import {
  checkProductPoster,
  checkProductVideo,
  productVideoPath,
  type VideoCheck,
} from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t, tn } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import {
  clearProductVideo,
  fetchProductVideoAdmin,
  productVideoUrl,
  updateRow,
  uploadProductVideoFile,
  type ProductVideoAdmin,
} from "../../lib/queries";
import { supabase, unwrap } from "../../lib/supabase";
import { ProductSelect } from "../estimate/EstimateCard";
import type { Product } from "@roof-hawk/core";

/**
 * A frame from the video, as a JPEG, without uploading anything first.
 *
 * One second in rather than zero: the first frame of a phone recording is
 * routinely the moment before the camera settled, and a still of a blurred
 * gutter sells nothing. A video shorter than that seeks to its own start.
 *
 * Resolves null rather than throwing. Every caller treats a missing poster as
 * a cosmetic loss, because it is.
 */
async function captureStill(file: File): Promise<Blob | null> {
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<Blob | null>((resolve) => {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.muted = true;
      video.playsInline = true;

      const give = (blob: Blob | null) => {
        video.removeAttribute("src");
        resolve(blob);
      };

      // A decode that never fires an event would otherwise hang the upload.
      const timer = setTimeout(() => give(null), 10_000);

      video.onerror = () => {
        clearTimeout(timer);
        give(null);
      };
      video.onloadedmetadata = () => {
        video.currentTime = Math.min(1, Math.max(0, video.duration - 0.1));
      };
      video.onseeked = () => {
        clearTimeout(timer);
        try {
          const canvas = document.createElement("canvas");
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const context = canvas.getContext("2d");
          if (!context || !canvas.width) return give(null);
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => give(blob), "image/jpeg", 0.82);
        } catch {
          give(null);
        }
      };

      video.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function ProductVideosTool() {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [adding, setAdding] = useState<Product | null>(null);

  const query = useAsync(async () => {
    const [withVideo, catalogue, company] = await Promise.all([
      fetchProductVideoAdmin(),
      supabase
        .from("products")
        .select("id, name, category, unit, kind, search_terms, times_ordered")
        .eq("active", true)
        .order("name"),
      supabase
        .from("company_settings")
        .select("work_gallery_url")
        .eq("id", 1)
        .maybeSingle(),
    ]);
    return {
      videos: withVideo,
      products: unwrap<Product[]>(catalogue),
      workUrl:
        (company.data as { work_gallery_url: string | null } | null)
          ?.work_gallery_url ?? "",
    };
  }, []);

  const videos = query.data?.videos ?? [];
  const products = query.data?.products ?? [];
  const workUrl = query.data?.workUrl ?? "";

  /**
   * Where the proposal's "See Our Work" page sends the customer.
   *
   * Here rather than on a settings screen because this is where somebody
   * looking for "the videos on the proposal" goes. It is a row on
   * `company_settings`, not a constant in the source, so moving the playlist
   * is an edit in this box (see migration 20260923200000).
   */
  const saveWorkUrl = (next: string) =>
    guard("work-url", async () => {
      const { error: failed } = await supabase
        .from("company_settings")
        .update({ work_gallery_url: next || null })
        .eq("id", 1);
      if (failed) throw new Error(failed.message);
    });

  const guard = async (label: string, action: () => Promise<void>) => {
    setBusy(label);
    try {
      await action();
      setError(null);
      await query.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  /** A refusal stops; a warning asks. Both sentences come from core. */
  const allowed = (check: VideoCheck): boolean => {
    if (!check.ok) {
      setError(t(check.problem));
      return false;
    }
    if (check.warning && !window.confirm(t(check.warning))) return false;
    setError(null);
    return true;
  };

  const uploadVideo = (product: { id: string; name: string }, file: File) => {
    if (!allowed(checkProductVideo(file))) return;
    void guard(product.id, async () => {
      // A new name for every upload: a proposal already sent keeps the URL of
      // whatever was live when it was frozen, so nothing is written twice.
      const at = new Date();
      const videoPath = productVideoPath(product.id, "video", at);
      await uploadProductVideoFile(videoPath, file, file.type || "video/mp4");
      // The product points at the video before the still is attempted. A
      // browser that will not decode the file — or takes ten seconds deciding
      // — then costs the poster and nothing else, and the walkthrough is live
      // either way. See the note at the top of this file.
      await updateRow("products", product.id, { video_path: videoPath });
      setAdding(null);

      const still = await captureStill(file);
      if (!still) return;
      const posterPath = productVideoPath(product.id, "poster", at);
      await uploadProductVideoFile(posterPath, still, "image/jpeg");
      await updateRow("products", product.id, {
        video_poster_path: posterPath,
      });
    });
  };

  const uploadPoster = (product: ProductVideoAdmin, file: File) => {
    if (!allowed(checkProductPoster(file))) return;
    void guard(product.id, async () => {
      const path = productVideoPath(product.id, "poster", new Date());
      await uploadProductVideoFile(path, file, file.type);
      await updateRow("products", product.id, { video_poster_path: path });
    });
  };

  /**
   * Clears the product and deletes nothing: every proposal already sent points
   * at this file, and must go on playing it. The sentence says exactly that.
   */
  const remove = (product: ProductVideoAdmin) => {
    if (
      !window.confirm(
        t(
          "Take the walkthrough off {name}? Proposals presented from now on will not show it. Proposals already sent keep playing it — the file itself is kept.",
          { name: product.name },
        ),
      )
    )
      return;
    void guard(product.id, () => clearProductVideo(product));
  };

  /** Which products can still be given one — the rest are already listed. */
  const withoutVideo = products.filter(
    (product) => !videos.some((video) => video.id === product.id),
  );

  return (
    <>
      <ErrorNote error={error} />

      <div className="toolbar2">
        <span className="count">
          {tn(
            videos.length,
            "1 product with a video",
            "{n} products with a video",
          )}
        </span>
      </div>

      <p className="na-hint">
        {t(
          "A product's walkthrough plays on the customer's proposal, directly under their inspection photographs, on every estimate that sells it. Keep them short and around 15 MB — a customer opens these on a phone. The still is taken from the video itself unless you upload one.",
        )}
      </p>

      <div className="row">
        <label>
          {t("“See Our Work” link")}{" "}
          <input
            type="url"
            placeholder="https://"
            size={46}
            disabled={busy !== null}
            defaultValue={workUrl}
            onBlur={(e) => {
              const next = e.target.value.trim();
              if (next === workUrl) return;
              void saveWorkUrl(next);
            }}
          />
        </label>
      </div>
      <p className="na-hint">
        {t(
          "The last page of every residential proposal, just above where the customer signs: one button that opens this. Leave it empty and that page is not shown at all. Commercial proposals never show it.",
        )}
      </p>

      <div className="vidadd">
        <ProductSelect
          products={withoutVideo}
          value={adding?.id ?? null}
          width={340}
          onChange={(product) => setAdding(product)}
        />
        {adding ? (
          <label className="btn-small filebtn">
            {busy === adding.id
              ? t("Uploading…")
              : t("Choose a video for {name}", { name: adding.name })}
            <input
              type="file"
              accept="video/mp4"
              disabled={busy !== null}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) uploadVideo(adding, file);
              }}
            />
          </label>
        ) : (
          <span className="na-hint">
            {t("Pick the product this video is of.")}
          </span>
        )}
      </div>

      <div className="ewrap">
        <table className="etable">
          <thead>
            <tr>
              <th>{t("Still")}</th>
              <th>{t("Product")}</th>
              <th>{t("What the customer reads over it")}</th>
              <th>{t("The line underneath")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {videos.length === 0 ? (
              <tr>
                <td colSpan={5} className="tnone">
                  {t("No product has a walkthrough yet.")}
                </td>
              </tr>
            ) : (
              videos.map((video) => (
                <tr key={video.id}>
                  <td>
                    <div className="vidcell">
                      {video.video_poster_path ? (
                        <img
                          src={productVideoUrl(video.video_poster_path)}
                          alt={t("Still from the {name} walkthrough", {
                            name: video.name,
                          })}
                        />
                      ) : (
                        <span className="nostill">{t("No still")}</span>
                      )}
                      <label className="linklike filebtn">
                        {t("Replace still")}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          disabled={busy !== null}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            e.target.value = "";
                            if (file) uploadPoster(video, file);
                          }}
                        />
                      </label>
                    </div>
                  </td>
                  <td>
                    <div className="vidname">
                      <strong>{video.name}</strong>
                      {video.video_path ? (
                        <a
                          href={productVideoUrl(video.video_path)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t("Watch it")}
                        </a>
                      ) : null}
                    </div>
                  </td>
                  <td>
                    <input
                      style={{ minWidth: 220 }}
                      defaultValue={video.video_title ?? ""}
                      placeholder={video.name}
                      onBlur={(e) =>
                        void guard(video.id, () =>
                          updateRow("products", video.id, {
                            video_title: e.target.value.trim() || null,
                          }),
                        )
                      }
                    />
                  </td>
                  <td>
                    <input
                      style={{ minWidth: 260 }}
                      defaultValue={video.video_caption ?? ""}
                      placeholder={t(
                        "A short walkthrough of the work, start to finish.",
                      )}
                      onBlur={(e) =>
                        void guard(video.id, () =>
                          updateRow("products", video.id, {
                            video_caption: e.target.value.trim() || null,
                          }),
                        )
                      }
                    />
                  </td>
                  <td>
                    <button
                      className="linklike danger"
                      disabled={busy !== null}
                      onClick={() => remove(video)}
                    >
                      {t("Remove")}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
